// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/crmService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  AtendimentoResumo,
  AtendimentoDetalhe,
  CriarAtendimentoRequest,
  AtualizarAtendimentoRequest,
  AdicionarContatoRequest,
  AtendimentoAnalitico,
  PaginadoResponse,
  Visao360,
} from '../types';

const BASE = '/api/crm';

export type OrdemAtendimentos = 'solicitacao' | 'numero' | 'assunto' | 'prioridade';

export interface ListaAtendimentosFiltros {
  busca?: string;
  /** 'pendente' | 'concluido' ('todos' ou vazio = todos). */
  situacao?: string;
  dtInicio?: string;
  dtFim?: string;
  ordenarPor?: OrdemAtendimentos;
  decrescente?: boolean;
}

export interface AtendimentosResumo {
  total: number;
  pendentes: number;
  concluidos: number;
  urgentes: number;
}

const paramsDoPeriodoEBusca = (filtros: ListaAtendimentosFiltros) => ({
  busca: filtros.busca?.trim() || undefined,
  dtInicio: filtros.dtInicio || undefined,
  dtFim: filtros.dtFim || undefined,
});

export const crmService = {
  /**
   * Lista paginada; busca (número, assunto ou cliente), situação, período da solicitação
   * (yyyy-MM-dd) e ordenação são feitos no servidor.
   */
  async getAll(
    pagina = 1,
    tamanhoPagina = 20,
    filtros: ListaAtendimentosFiltros = {},
  ): Promise<PaginadoResponse<AtendimentoResumo>> {
    const { data } = await apiClient.get<PaginadoResponse<AtendimentoResumo>>(BASE, {
      params: {
        pagina, tamanhoPagina,
        ...paramsDoPeriodoEBusca(filtros),
        situacao: filtros.situacao && filtros.situacao !== 'todos' ? filtros.situacao : undefined,
        ordenarPor: filtros.ordenarPor,
        decrescente: filtros.decrescente,
      },
    });
    return data;
  },

  /** KPIs do período/busca (sem o filtro de situação). */
  async getResumo(filtros: ListaAtendimentosFiltros = {}): Promise<AtendimentosResumo> {
    const { data } = await apiClient.get<AtendimentosResumo>(`${BASE}/resumo`, {
      params: paramsDoPeriodoEBusca(filtros),
    });
    return data;
  },

  async getById(id: number): Promise<AtendimentoDetalhe> {
    const { data } = await apiClient.get<AtendimentoDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarAtendimentoRequest): Promise<AtendimentoDetalhe> {
    const { data } = await apiClient.post<AtendimentoDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarAtendimentoRequest): Promise<AtendimentoResumo> {
    const { data } = await apiClient.put<AtendimentoResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async adicionarContato(id: number, request: AdicionarContatoRequest): Promise<AtendimentoAnalitico> {
    const { data } = await apiClient.post<AtendimentoAnalitico>(`${BASE}/${id}/contatos`, request);
    return data;
  },

  async removerContato(atendimentoId: number, contatoId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${atendimentoId}/contatos/${contatoId}`);
  },

  async visao360(clienteId: number): Promise<Visao360> {
    const { data } = await apiClient.get<Visao360>(`${BASE}/visao360/${clienteId}`);
    return data;
  },
};
